/**
 * AnyClip — 전역 상태 관리
 * useContext + useReducer 패턴
 *
 * 상태(State):
 *   status: 'idle' | 'loading' | 'success' | 'error' | 'referral_locked'
 *   streamUrl: 다운로드 가능한 URL
 *   referralLink: 공유용 레퍼럴 링크
 *   errorMsg: 에러 메시지
 *   downloadCount: 현재 다운로드 횟수
 *   isUnlocked: 언락 여부
 *   user: Telegram WebApp user 객체
 */

import React, { createContext, useContext, useReducer } from 'react';

// ── 초기 상태 ─────────────────────────────────────────────
const initialState = {
  status: 'idle',
  type: 'video', // 'video', 'image', 'carousel'
  streamUrl: null,
  mediaUrls: [],
  referralLink: null,
  errorMsg: null,
  downloadCount: 0,
  tickets: 0,
  user: null,
};

// ── 액션 타입 ──────────────────────────────────────────────
export const ACTIONS = {
  SET_LOADING:        'SET_LOADING',
  SET_SUCCESS:        'SET_SUCCESS',
  SET_REFERRAL_LOCKED:'SET_REFERRAL_LOCKED',
  SET_ERROR:          'SET_ERROR',
  SET_USER:           'SET_USER',
  SET_TICKETS:        'SET_TICKETS',
  RESET:              'RESET',
};

// ── 리듀서 ────────────────────────────────────────────────
function appReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, status: 'loading', errorMsg: null, streamUrl: null, mediaUrls: [], type: 'video' };

    case ACTIONS.SET_SUCCESS:
      return {
        ...state,
        status: 'success',
        streamUrl: action.payload.streamUrl,
        type: action.payload.type || 'video',
        mediaUrls: action.payload.mediaUrls || [],
        downloadCount: action.payload.downloadCount,
        tickets: action.payload.tickets,
        errorMsg: null,
      };

    case ACTIONS.SET_REFERRAL_LOCKED:
      return {
        ...state,
        status: 'referral_locked',
        referralLink: action.payload?.referralLink || state.referralLink,
        downloadCount: action.payload?.downloadCount || state.downloadCount,
        tickets: action.payload?.tickets ?? state.tickets,
        streamUrl: null,
      };

    case ACTIONS.SET_ERROR:
      return { ...state, status: 'error', errorMsg: action.payload.message };

    case ACTIONS.SET_USER:
      return { ...state, user: action.payload };

    case ACTIONS.SET_TICKETS:
      return { 
        ...state, 
        tickets: action.payload.tickets !== undefined ? action.payload.tickets : action.payload,
        downloadCount: action.payload.downloadCount !== undefined ? action.payload.downloadCount : state.downloadCount
      };

    case ACTIONS.RESET:
      return { ...initialState, user: state.user, downloadCount: state.downloadCount, tickets: state.tickets };

    default:
      return state;
  }
}

// ── 컨텍스트 생성 ──────────────────────────────────────────
const AppContext = createContext(null);

// ── 프로바이더 ─────────────────────────────────────────────
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

// ── 커스텀 훅 ──────────────────────────────────────────────
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within <AppProvider>');
  return ctx;
}
